import React, { Component } from 'react';

// Components and Others
import { clone } from 'lodash';
import PropTypes from 'prop-types';
import ZipUtils from 'utils/ZipUtils';
import DateUtils from 'lib/DateUtils';
import StringUtils from 'lib/StringUtils';
import ModalUtils from 'utils/ModalUtils';
import { DefaultCountry } from 'utils/enum/Customer';
import CustomerMapper from 'services/mapData/CustomerMapper';

// GraphQL
import CustomerMap from 'services/mapData/CustomerMap';
import GraphQLClient from 'services/apollo/GraphQLClient';
import CustomerService from 'services/modules/CustomerService';
import CustomerQuery from 'services/graphQL/query/CustomerQuery';

const EmploymentDialogContainer = (WrappedComponent) => class extends Component {
    static propTypes = {
        isEditing: PropTypes.bool,
        record: PropTypes.object,
        onClose: PropTypes.func.isRequired,
        setDealCurrentAddress: PropTypes.func,
        customerId: PropTypes.string.isRequired,
    };

    static defaultProps = {
        isEditing: false,
        record: {},
        setDealCurrentAddress: false,
    }

    constructor(props) {
        super(props);
        this.graphqlClient = new GraphQLClient();
        this.customerService = new CustomerService();
        this.graphqlClient = new GraphQLClient();
        const newRecord = props.isEditing ? CustomerMapper.mapEmploymentData(props.record) : this.getInitState();
        const cities = [];
        if (newRecord && newRecord.city) {
            cities.push(newRecord.city);
        }

        this.state = {
            zipData: [],
            listCity: cities,
            openConfirm: false,
            record: newRecord,
            isValidEmployment: false,
            isDecoderEnabled: false,
            isDecodingZip: false,
        };

        this.initBind();
    }

    componentDidMount() {
        const { isEditing, record: { zipCode } } = this.props;

        if (isEditing && !StringUtils.isEmpty(zipCode)) {
            this.decodeZip(zipCode, true);
        }
    }

    onEnableDecoder() {
        const { state: { isDecoderEnabled } } = this;
        if (!isDecoderEnabled) {
            this.setState({ isDecoderEnabled: true });
        }
    }

    onChangeValue(field, value) {
        this.setState(({ record }) => {
            const newRecord = { ...record };
            newRecord[field] = value;
            return { record: newRecord };
        }, () => {
            if (field === 'zipCode' && value.length === 5) {
                const { state: { isDecoderEnabled } } = this;
                if (isDecoderEnabled) {
                    this.decodeZip(value);
                }
            }
        });
    }

    onChangeCity(field, value) {
        this.setState(({ record, zipData }) => {
            const newRecord = { ...record };
            if (zipData && zipData.length > 0) {
                const alreadyExist = zipData.find((ele) => StringUtils.toUpperCase(ele.city) === StringUtils.toUpperCase(value));
                if (!alreadyExist) {
                    const newData = clone(zipData[0]);
                    newData.city = value;
                    zipData.unshift(newData);
                }
            }

            const countyAndState = ZipUtils.getCountryAndStateByCity(value, zipData);
            newRecord.city = value;
            if (!StringUtils.isEmpty(countyAndState.county)) newRecord.county = countyAndState.county;
            if (!StringUtils.isEmpty(countyAndState.state)) newRecord.state = countyAndState.state;

            return { record: newRecord };
        });
    }

    onChangeEndDate(value) {
        const { state: { record } } = this;
        const backUp = clone(record);
        backUp.end = value;

        if (DateUtils.isValid(value)) {
            backUp.isCurrentEmployment = false;
        }

        this.setState({
            record: backUp,
        });
    }

    onCloseDialogConfirm() {
        this.setState({
            openConfirm: false,
        });
    }

    onSave(input) {
        const { props: { onClose, setDealCurrentAddress } } = this;

        this.customerService.createCustomerEmployment(input)
            .then((response) => {
                const { data, graphQLErrors } = response;

                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }

                if (data && data.createCustomerEmployment) {
                    if (setDealCurrentAddress) setDealCurrentAddress(data.createCustomerEmployment);
                    onClose();
                }
            });
    }

    onUpdate(input) {
        const { props: { onClose, setDealCurrentAddress } } = this;

        this.customerService.updateCustomerEmployment(input)
            .then((response) => {
                const { data, graphQLErrors } = response;

                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);

                    return;
                }

                if (data && data.updateCustomerEmployment) {
                    if (setDealCurrentAddress) setDealCurrentAddress(data.updateCustomerEmployment);
                    onClose();
                }
            });
    }

    getInitState() {
        const { props: { setDealCurrentAddress } } = this;
        return {
            employer: '',
            title: '',
            workPhone: '',
            monthlyIncome: null,
            otherIncomeMonthly: 0,
            otherIncomeSource: '',
            end: '',
            start: '',
            isCurrentEmployment: setDealCurrentAddress !== null,
            address1: '',
            address2: '',
            zipCode: null,
            city: '',
            county: '',
            state: '',
            country: DefaultCountry.COUNTRY,
        };
    }

    getData(isCurrentEmployment) {
        const { state: { record }, props: { customerId } } = this;

        const input = {
            customerId,
            input: {
                employer: record.employer,
                title: record.title,
                workPhone: record.workPhone,
                monthlyIncome: record.monthlyIncome,
                otherIncomeMonthly: record.otherIncomeMonthly,
                otherIncomeSource: record.otherIncomeSource,
                start: DateUtils.formatUTC(record.start),
                end: DateUtils.formatUTC(record.end),
                address1: record.address1,
                address2: record.address2,
                zipCode: StringUtils.isEmpty(record.zipCode) ? '' : record.zipCode.toString(),
                isCurrentEmployment: isCurrentEmployment !== null ? isCurrentEmployment : record.isCurrentEmployment,
                city: record.city,
                county: record.county,
                state: record.state,
                country: record.country,
            },
        };

        return input;
    }

    decodeZip(zip = '', ignoreCity = false) {
        const input = {
            zip,
        };

        this.setState({ isDecodingZip: true });
        this.graphqlClient
            .query(CustomerQuery.DECODE_ZIP_CODE, input)
            .then((response) => {
                const { data, graphQLErrors } = response;

                if (graphQLErrors) {
                    ModalUtils.errorMessage(graphQLErrors);
                    return;
                }

                if (data?.decodeZip) {
                    const { decodeZip } = data;

                    /* In order to keep the city value, we need
                    to assign it using the record from database */
                    if (ignoreCity && decodeZip.length > 0) {
                        const { state: { record } } = this;
                        const alreadyExist = decodeZip.find((ele) => StringUtils.toUpperCase(ele.city) === StringUtils.toUpperCase(record.city));

                        if (!alreadyExist) {
                            const newData = clone(decodeZip[0]);
                            newData.city = record.city;
                            decodeZip.unshift(newData);
                        }
                    }

                    const listCity = decodeZip.map((item) => CustomerMap.mapCity(item));
                    if (listCity.length > 1) listCity.unshift({ label: 'None', value: '' });
                    const zipData = decodeZip;

                    this.setState(({ record, isDecoderEnabled }) => {
                        if (isDecoderEnabled) {
                            const newRecord = {
                                ...record,
                                city: '',
                                county: '',
                                state: '',
                            };
                            return {
                                listCity, zipData, record: newRecord, isDecodingZip: false,
                            };
                        }
                        return { listCity, zipData, isDecodingZip: false };
                    });

                    if (decodeZip.length === 1) {
                        const currentData = decodeZip[0];

                        this.setState(({ record }) => {
                            const newRecord = {
                                ...record,
                                city: currentData.city,
                                county: currentData.county,
                                state: currentData.state,
                            };

                            return { record: newRecord };
                        });
                    }
                }
            });
    }

    // TODO: Review this method when creating the edit
    saveOrEdit(isCurrentEmployment = null) {
        const { props: { isEditing, record: { customerEmploymentId } } } = this;
        const input = this.getData(isCurrentEmployment);

        if (isEditing) {
            input.customerEmploymentId = customerEmploymentId;
            this.onUpdate(input);
        } else {
            this.onSave(input);
        }
    }

    beforeSaving() {
        const { state: { record: { end, isCurrentEmployment } } } = this;

        if (!DateUtils.isValid(end) && !isCurrentEmployment) {
            this.setState({
                openConfirm: true,
            });

            return;
        }

        this.saveOrEdit();
    }

    initBind() {
        this.decodeZip = this.decodeZip.bind(this);
        this.saveOrEdit = this.saveOrEdit.bind(this);
        this.onChangeCity = this.onChangeCity.bind(this);
        this.beforeSaving = this.beforeSaving.bind(this);
        this.onChangeValue = this.onChangeValue.bind(this);
        this.onChangeEndDate = this.onChangeEndDate.bind(this);
        this.onCloseDialogConfirm = this.onCloseDialogConfirm.bind(this);
        this.onEnableDecoder = this.onEnableDecoder.bind(this);
    }

    render() {
        const { props, state } = this;

        return (
            <WrappedComponent
                {...props}
                {...state}
                saveOrEdit={this.saveOrEdit}
                beforeSaving={this.beforeSaving}
                onChangeCity={this.onChangeCity}
                onChangeValue={this.onChangeValue}
                onChangeEndDate={this.onChangeEndDate}
                onCloseDialogConfirm={this.onCloseDialogConfirm}
                onEnableDecoder={this.onEnableDecoder}
            />
        );
    }
};

export default EmploymentDialogContainer;
